import { Button, ButtonGroup } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { filterOperators } from 'components/enhanced/data-grid/utils/filters';
import { IsSuperAdmin } from 'shared/permissions/IsSuperAdmin';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import TenantPage from 'layouts/TenantPage';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { loadingActions } from 'store';
import { useAppDispatch, useAppSelector } from 'store/store';
import { StatusOption } from 'shared/types/general';
import toast from 'shared/utils/toast';
import ChangeOrderStatusForm from '../components/PurchaseOrderStatusChangeForm';
import { IPurchaseOrder } from '../types';
import { orderService } from '../api/order.service';
import { baseCell } from 'components/cells/BaseCell';
import { EnhancedDialog } from 'components/enhanced';
import ImportFileUploader from 'components/ui/file-uploader/ImportFileUploader';
import { PurchaseOrderColumns } from '../purchase-orders';

export const PurchaseOrderList = () => {
  const translationPrefix = 'pages.orders.list';
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const refresh = useRef(null);

  const sap_active = useAppSelector((state) => state.auth.sap_active);
  const navigate = useNavigate();

  const [selectedOrder, setSelectedOrder] = useState<IPurchaseOrder>();
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const isAdmin = useIsTenantAdministrator();

  // Status edit dialog
  const [statusEditDialogOpen, setStatusEditDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(loadingActions.startLoading());
    orderService.options().then((res) => {
      if (res.actions && res.actions.POST && res.actions.POST.status.choices) {
        const choices = res.actions.POST.status.choices
          .filter((choice) => choice.value !== 0)
          .map((choice) => ({
            ...choice,
            display_name: t(`entities.order_head.${choice.display_name}`)
          }));
        setStatusOptions(choices);
      }
    });
    dispatch(loadingActions.stopLoading());
  }, []);

  // ----------------------------------------------------------- UPDATE STATUS
  const openChangeStatus = (row: any) => {
    if (row.sap_id != null && row.status == 'COMPLETED') {
      toast.error(t(`${translationPrefix}.error-sap-edit`));
    } else {
      setSelectedOrder(row);
      setStatusEditDialogOpen(true);
    }
    // Apro il dialog per modificare lo stato
    if (isAdmin) {
      setStatusEditDialogOpen(true);
    }
  };

  const handleCloseChangeStatusDialog = () => {
    // Chiudo il dialog per modificare lo stato
    setSelectedOrder(null);
    setStatusEditDialogOpen(false);
  };

  const onEditOrderStatus = (status: any) => {
    orderService.update(selectedOrder.id, status).then((res) => {
      toast.success(t('actions.successfully.updated'));
      refresh.current();

      // Chiudo il dialog per modificare lo stato
      handleCloseChangeStatusDialog();
    });
  };

  // ----------------------------------------------------------- Actions Method
  const editOrder = (reqId) => {
    navigate(`../orders/view/${reqId}`);
  };

  const downloadAttachment = (order) => {
    if (order?.id) {
      orderService.downloadAttachment(order.id);
    }
  };

  const regenerateDocument = (order) => {
    if (order?.id) {
      orderService.regeneratePDF(order.id).then(() => {
        toast.success('PDF rigenerato.');
      });
    }
  };

  const columns: GridColDef[] = PurchaseOrderColumns(
    statusOptions,
    openChangeStatus,
    editOrder,
    downloadAttachment,
    regenerateDocument
  );

  if (sap_active) {
    columns.push({
      field: 'sap_id',
      headerName: t(`global.sap_id`),
      filterOperators,
      width: 100,
      renderCell: baseCell
    });
  }

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      menuRight={
        <ButtonGroup size="small">
          <NavLink to="delivery?tab=in-arrivo">
            <Button>{t(`${translationPrefix}.lines_in_arrivo`)}</Button>
          </NavLink>
          <NavLink to="delivery?tab=awaiting-confirmation">
            <Button>{t(`${translationPrefix}.lines_awaiting_confirmation`)}</Button>
          </NavLink>
          <IsSuperAdmin>
            <ImportFileUploader
              service={orderService}
              importMethod={orderService.importAggregatedLinesFromFile}
              templateGetterMethod={orderService.getAggregatedImportTemplate}
            />
          </IsSuperAdmin>
        </ButtonGroup>
      }>
      <EnhancedDataGrid
        columns={columns}
        service={orderService}
        warehouseFilterParam="warehouse"
        refresh={refresh}
      />

      {selectedOrder && (
        <EnhancedDialog
          open={statusEditDialogOpen}
          onClose={handleCloseChangeStatusDialog}
          fullWidth
          maxWidth="sm"
          title={t('entities.order_head.edit-status-title', { ref: selectedOrder.ref })}
          submitButtonProps={{
            type: 'submit',
            form: 'editOrderStatus'
          }}>
          {/* Form for inserting new documents */}
          <ChangeOrderStatusForm onSubmit={onEditOrderStatus} status={statusOptions} />
        </EnhancedDialog>
      )}
    </TenantPage>
  );
};

export default PurchaseOrderList;
